import React, { useCallback, useMemo } from "react";
import SubmenuItems from "../../components/Header/SubmenuItems";
import { HiveIcons } from "../../assets/icons";
import FilterBase from "../../components/Common/FilterBase";
import TableFilters from "../../components/Common/TableFilters";
import {
  AFFECTED_PRODUCTS,
  ATTACK_EXECUTORS,
  ATTACK_PHASE_OPTIONS,
  ATTACK_STATUS,
  ATTACK_SUPPORTED_SYSTEMS,
  MONTH_OPTIONS,
  RANK_ENTITY,
  TARGET_TYPES,
  YES_NO_OPTIONS,
} from "../../constants/enums";
import { useTableSearch } from "../../helpers/hooks";
import SearchBox from "../../components/Common/SearchBox";
import { AccountTreeOutlined } from "@material-ui/icons";
import RankButton from "../../components/Common/Ranking/RankButton";
import { useSelector } from "react-redux";
import { createMap } from "reactcoregk/utils";
import { getParameterByName, updateURLParameter } from "../../helpers/utils";
import { TextField } from "@material-ui/core";
import MultiSelect from "../../components/Inputs/MultiSelect";

const year = new Date().getFullYear();
const year_options = Array.from(new Array(20), (val, index) => {
  return {
    label: year - index,
    value: year - index,
  };
});

const filterOptions = (categoryAndTypes = [[], []]) => {
  return [
    {
      label: "Category",
      value: "category",
      multiple: true,
      options: categoryAndTypes[0]?.map((item) => ({
        label: item,
        value: item,
      })),
    },
    {
      label: "Category Type",
      value: "categoryType",
      multiple: true,
      options: categoryAndTypes[1]?.map((item) => ({
        label: item,
        value: item,
      })),
    },

    {
      label: "Attack Phases",
      value: "attackPhases",
      options: ATTACK_PHASE_OPTIONS,
    },

    {
      label: "Target",
      value: "target",
      options: TARGET_TYPES,
    },
    {
      label: "Status",
      value: "status",
      options: ATTACK_STATUS,
    },
    {
      customEl: (locationSearch, setLocationSearch) => (
        <CreatedDate
          locationSearch={locationSearch}
          setLocationSearch={setLocationSearch}
        />
      ),
    },
    {
      label: "Created Month",
      value: "createdAtMonth",
      options: MONTH_OPTIONS,
    },
    {
      label: "Created Year",
      value: "createdAtYear",
      options: year_options,
    },
    {
      customEl: (locationSearch, setLocationSearch) => (
        <UpdatedDate
          locationSearch={locationSearch}
          setLocationSearch={setLocationSearch}
        />
      ),
    },
    {
      label: "Updated Month",
      value: "updatedAtMonth",
      options: MONTH_OPTIONS,
    },
    {
      label: "Updated Year",
      value: "updatedAtYear",
      options: year_options,
    },
    {
      label: "Sigma File Present",
      value: "isSigmaFilePresent",
      options: YES_NO_OPTIONS,
    },
    {
      label: "Additional File Present",
      value: "isAdditionalFilePresent",
      options: YES_NO_OPTIONS,
    },
    {
      label: "Payload File Present",
      value: "isPayloadFilePresent",
      options: YES_NO_OPTIONS,
    },
    {
      customEl: (locationSearch, setLocationSearch) => (
        <SupportedPlatforms
          locationSearch={locationSearch}
          setLocationSearch={setLocationSearch}
        />
      ),
    },
    {
      customEl: (locationSearch, setLocationSearch) => (
        <Executors
          locationSearch={locationSearch}
          setLocationSearch={setLocationSearch}
        />
      ),
    },
    {
      customEl: (locationSearch, setLocationSearch) => (
        <AffectedPrograms
          locationSearch={locationSearch}
          setLocationSearch={setLocationSearch}
        />
      ),
    },
  ];
};

function getAttacks(state) {
  return createMap(state.Attack.getAllPageable.all);
}

const Subheader = ({
  handleCreateNew,
  selected,
  openSettings,
  categoryAndTypes = [[], []],
}) => {
  const [query, handleChange, syncQuery] = useTableSearch();
  const allAttacks = useSelector(getAttacks);

  const items = useMemo(
    () =>
      selected.map((id) => ({
        id,
        entityName: RANK_ENTITY.BAS_ATTACKS,
        name: allAttacks.get(id)?.name,
      })),
    [selected, allAttacks]
  );

  const menuItems = useMemo(
    () => [
      {
        title: "Add Mitre TTP",
        icon: <AccountTreeOutlined style={{ marginRight: 4 }} />,
        path: "/mitre-ttp",
      },
      {
        pushRight: true,
        customEl: (
          <RankButton
            selected={items}
            entityName={"Attack"}
            rankEntity={RANK_ENTITY.BAS_ATTACKS}
          />
        ),
      },
      {
        customEl: (
          <SearchBox
            query={query}
            handleChange={handleChange}
            syncQuery={syncQuery}
          />
        ),
      },
      {
        customEl: (
          <FilterBase>
            <TableFilters filterOptions={filterOptions(categoryAndTypes)} />
          </FilterBase>
        ),
      },
      { divider: true },
      {
        title: "New Attack",
        icon: <HiveIcons.AddOutlined />,
        onClick: handleCreateNew,
      },
      {
        title: "Customize View",
        onClick: openSettings,
      },
    ],
    [
      items,
      query,
      handleChange,
      syncQuery,
      handleCreateNew,
      openSettings,
      categoryAndTypes,
    ]
  );

  return <SubmenuItems items={menuItems} />;
};

function DateFilter({ locationSearch, setLocationSearch, paramName, label }) {
  const handleChange = useCallback(
    (e) => {
      const searchDate = updateURLParameter(
        locationSearch,
        paramName,
        e.target.value
      );
      setLocationSearch(searchDate);
    },
    [locationSearch, setLocationSearch, paramName]
  );

  return (
    <TextField
      variant="standard"
      margin="normal"
      type="date"
      fullWidth
      sx={{ width: "300px" }}
      value={getParameterByName(paramName, locationSearch) || ""}
      onChange={handleChange}
      label={label}
      color="primary"
      InputLabelProps={{ shrink: true }}
    />
  );
}

function CreatedDate(props) {
  return <DateFilter {...props} paramName="createdAt" label="Created Date" />;
}

function UpdatedDate(props) {
  return <DateFilter {...props} paramName="updatedAt" label="Updated Date" />;
}

function MultiSelectFilter({
  locationSearch,
  setLocationSearch,
  paramName,
  label,
  placeholder,
  controlId,
  options,
}) {
  const handleChange = useCallback(
    (selectedValues) => {
      const search = updateURLParameter(
        locationSearch,
        paramName,
        selectedValues.join(",")
      );
      setLocationSearch(search);
    },
    [locationSearch, setLocationSearch, paramName]
  );

  const paramValue = getParameterByName(paramName, locationSearch) || "";
  const values = paramValue.split(",").filter(Boolean);

  return (
    <MultiSelect
      fullWidth
      margin="dense"
      onChange={handleChange}
      values={values}
      label={label}
      placeholder={placeholder}
      controlId={controlId}
      options={options}
      labelId={controlId}
    />
  );
}

function SupportedPlatforms(props) {
  return (
    <MultiSelectFilter
      {...props}
      paramName="supportedPlatform"
      label="Supported Platforms"
      placeholder="Select Platforms"
      controlId="platforms"
      options={ATTACK_SUPPORTED_SYSTEMS}
    />
  );
}

function Executors(props) {
  return (
    <MultiSelectFilter
      {...props}
      paramName="executors"
      label="Executors"
      placeholder="Select executors"
      controlId="executors"
      options={ATTACK_EXECUTORS}
    />
  );
}

function AffectedPrograms(props) {
  return (
    <MultiSelectFilter
      {...props}
      paramName="affectedPrograms"
      label="Affected Programs"
      placeholder="Select programs"
      controlId="programs"
      options={AFFECTED_PRODUCTS}
    />
  );
}

export default Subheader;
